import React from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Paper,
} from "@mui/material";
import { 
  CalendarToday, 
  Schedule, 
  Settings,
  TrendingUp,
  AccessTime,
  BookOnline,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function SolutionSection() {
  const navigate = useNavigate();
  
  const benefits = [
    {
      icon: <CalendarToday sx={{ fontSize: 40, color: "#2563eb" }} />,
      title: "AI-Powered Appointment Scheduler",
      description: "Patients book 24/7, even when you're closed. No more missed calls = more revenue",
      result: "Dr. Martinez reported a 23% increase in after-hours bookings!",
      color: "#2563eb",
      bgColor: "#eff6ff",
      borderColor: "#bfdbfe"
    },
    {
      icon: <AccessTime sx={{ fontSize: 40, color: "#059669" }} />,
      title: "Significant Time Savings",
      description: "Reclaim 6-8 hours weekly from tedious administrative tasks",
      result: "Dr. Martinez gained 8+ hours weekly back, allowing him to leave the office 2 hours earlier every day!",
      color: "#059669",
      bgColor: "#f0fdf4",
      borderColor: "#bbf7d0"
    },
    {
      icon: <Settings sx={{ fontSize: 40, color: "#7c3aed" }} />,
      title: "Streamlined Operations",
      description: "35% fewer scheduling conflicts and zero billing errors in month 1",
      result: "Perfect billing accuracy and seamless patient flow from day one!",
      color: "#7c3aed",
      bgColor: "#faf5ff",
      borderColor: "#d8b4fe"
    }
  ];

  return (
    <Box sx={{ backgroundColor: "#ffffff", py: 10 }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              mb: 3, 
              color: "#1f2937",
              fontSize: { xs: "2.5rem", lg: "3rem" },
              fontWeight: 700,
            }}
          >
            The Transformation Awaits
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#6b7280",
              maxWidth: 800,
              mx: "auto",
              fontSize: "20px",
              lineHeight: 1.6,
              mb: 4,
            }}
          >
            Imagine a practice where every patient can book an appointment 24/7, even when you're asleep. 
            Imagine an effortless system that handles the grunt work, so you can focus on what you love: patient care.
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} lg={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  textAlign: "center",
                  p: 4,
                  border: `2px solid ${benefit.borderColor}`,
                  borderRadius: 3,
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  },
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box 
                    sx={{ 
                      width: 64, 
                      height: 64, 
                      backgroundColor: benefit.bgColor, 
                      borderRadius: "50%", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      mx: "auto", 
                      mb: 3 
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#1f2937", fontSize: "20px" }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", lineHeight: 1.6, fontSize: "16px", mb: 3 }}
                  >
                    {benefit.description}
                  </Typography>
                  
                  <Box
                    sx={{
                      backgroundColor: "#f9fafb",
                      borderRadius: 2,
                      p: 2,
                      borderLeft: `4px solid ${benefit.color}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#374151", mb: 1, fontSize: "14px" }}
                    >
                      Proven Results:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: benefit.color, fontWeight: 500, fontSize: "14px" }}
                    >
                      "{benefit.result}"
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Success Story */}
        <Box
          sx={{
            position: "relative",
            borderRadius: 4,
            overflow: "hidden",
            background: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://readdy.ai/api/search-image?query=Happy%20successful%20optometrist%20doctor%20in%20modern%20clinic%20examining%20patient%20with%20advanced%20eye%20equipment%2C%20smiling%20professional%20in%20white%20coat%2C%20bright%20modern%20optometry%20office%20with%20satisfied%20patients%2C%20success%20story%20photography%20with%20warm%20professional%20lighting&width=1200&height=600&seq=success-story-001&orientation=landscape')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "white",
            p: { xs: 6, lg: 8 },
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  fontWeight: 700,
                  fontSize: { xs: "2rem", lg: "2.5rem" },
                }}
              >
                Dr. Martinez's Success Story
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  fontStyle: "italic",
                  mb: 4,
                  fontSize: { xs: "18px", lg: "22px" },
                  lineHeight: 1.6,
                }}
              >
                "Within 30 days, I was seeing 40% more patients while working 2 hours less each day. 
                The AI scheduler handles everything automatically, and my staff can focus on patient care 
                instead of phone calls. This software didn't just change my practice – it gave me my life back."
              </Typography>
              
              <Grid container spacing={4} sx={{ mb: 4, justifyContent: "center" }}>
                <Grid item xs={4} sm={2}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h3" sx={{ color: "#10b981", fontWeight: 700 }}>
                      40%
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "14px" }}>
                      More Patients
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h3" sx={{ color: "#60a5fa", fontWeight: 700 }}>
                      8
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "14px" }}>
                      Hours Saved Weekly
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h3" sx={{ color: "#fbbf24", fontWeight: 700 }}>
                      23%
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "14px" }}>
                      After-Hours Bookings
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "#15803d",
                  px: 6,
                  py: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  transform: "scale(1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#059669",
                    transform: "scale(1.05)",
                  },
                }}
                startIcon={<BookOnline />}
                onClick={() => navigate("/auth/login")}
              >
                Get Your Transformation Started
              </Button>
            </Box>
          </Container>
        </Box>
      </Container>
    </Box>
  );
}
